import React from 'react';
import {
  ActivityIndicator,
  Text as NativeText,
  StyleSheet,
  View,
} from 'react-native';
import {useAsyncFn} from 'react-use';

import {Button} from '../../components/Button';
import {ErrorMessage} from '../../components/Form/ErrorMessage';
import {Text} from '../../components/Typography';
import {
  ErrorMessages,
  Messages,
  Strings,
  interpolate,
  interpolateWithNode,
  texts,
} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import {Connection} from '../../contexts/connection';
import {userApi} from '../../lib/api/apis';
import {useThrottleFunction} from '../../lib/hooks/useThrottleFunction';
import type {RootStackScreenProps} from '../../navigators/RootStackNavigator';
import {toHome, toStartPairing} from '../../navigators/util';
import {centerText, centeredRowStyle, underlineText} from '../../styles';
import {LogoHeader, LogoTitle} from '../components';
import {Layout, TitleContainer} from '../components/composable';
import {authStyles} from './styles';

export interface Props {
  email?: string;
}

interface ScreenProps
  extends RootStackScreenProps<typeof Screens.PasswordResetSent> {}

export const PasswordResetSentScreen: React.FC<ScreenProps> = ({
  route,
  navigation,
}) => {
  const {peak} = Connection.useContainer();

  const connected = !!peak;

  const email = route?.params?.email ?? '';

  const [{error, loading}, resend] = useAsyncFn(
    () =>
      userApi.requestPasswordReset({
        userRequestResetPasswordDto: {email},
      }),
    [email],
  );

  const {trigger, cooldown} = useThrottleFunction(59);

  const onPress = () =>
    navigation.navigate(Screens.Login, {
      redirect: !connected ? toHome : toStartPairing(),
    });

  const blocked = cooldown > 0;

  return (
    <Layout>
      <View style={authStyles.innerContainer}>
        <LogoHeader>
          <TitleContainer>
            <LogoTitle>{texts.screens.passwordResetSent.title}</LogoTitle>
          </TitleContainer>
        </LogoHeader>

        <Text variant="s" style={centerText}>
          {interpolateWithNode(texts.screens.passwordResetSent.subtitle, {
            email: <NativeText style={underlineText}>{email}</NativeText>,
          })}
        </Text>

        <View style={authStyles.bodyContainer}>
          {!!error && (
            <ErrorMessage>{ErrorMessages.NETWORK_ERROR}</ErrorMessage>
          )}

          <Button {...{onPress}}>{Strings.DEFAULT_BTN}</Button>

          <View style={[centeredRowStyle, styles.retryContainer]}>
            <Text variant="s" style={centerText}>
              {texts.screens.passwordResetSent.helper}{' '}
              <Button
                disabled={loading || cooldown > 0}
                as={NativeText}
                variant="transparent"
                style={underlineText}
                onPress={() => trigger(resend)}>
                {blocked && !loading
                  ? interpolate(Messages.RETRY_IN, {
                      sec: Math.ceil(cooldown / 1000),
                    })
                  : Strings.RESEND}
              </Button>
            </Text>

            {loading && <ActivityIndicator />}
          </View>
        </View>
      </View>
    </Layout>
  );
};

const styles = StyleSheet.create({
  retryContainer: {
    gap: 8,
  },
});
